import { Component } from '@theme/component';

const STORAGE_KEY = 'katshu:wishlist';
const CHANGE_EVENT = 'wishlist:change';

/** @returns {string[]} */
function readWishlist() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function writeWishlist(handles) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

/** @param {string} handle */
export function isWishlisted(handle) {
  return readWishlist().includes(handle);
}

/**
 * @param {string} handle
 * @returns {boolean} Whether the product is now in the wishlist.
 */
export function toggleWishlist(handle) {
  const handles = readWishlist();
  const index = handles.indexOf(handle);

  if (index === -1) {
    handles.push(handle);
  } else {
    handles.splice(index, 1);
  }

  writeWishlist(handles);
  return handles.includes(handle);
}

export function getWishlist() {
  return readWishlist();
}

/**
 * A heart-shaped toggle that adds/removes `data-product-handle` from a
 * browser-local wishlist. No account or app required; state lives in
 * localStorage and syncs across every button on the page via a shared event.
 */
export class WishlistButton extends Component {
  requiredRefs = [];

  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    this.addEventListener('click', this.#handleActivate);
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
  }

  /** @param {MouseEvent} event */
  #handleActivate = (event) => {
    event.preventDefault();
    const handle = this.dataset.productHandle;
    if (!handle) return;

    const active = toggleWishlist(handle);
    this.#applyState(active);
  };

  #onChange = () => this.#sync();

  #sync() {
    const handle = this.dataset.productHandle;
    this.#applyState(handle ? isWishlisted(handle) : false);
  }

  /** @param {boolean} active */
  #applyState(active) {
    this.classList.toggle('is-active', active);
    this.querySelector('button')?.setAttribute('aria-pressed', String(active));
  }
}

customElements.define('wishlist-button', WishlistButton);

function updateCountBadges() {
  const count = getWishlist().length;

  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = count > 0 ? String(count) : '';
    el.classList.toggle('hidden', count === 0);
  });
}

document.addEventListener(CHANGE_EVENT, updateCountBadges);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', updateCountBadges, { once: true });
} else {
  updateCountBadges();
}
